import React, { useState, useEffect } from 'react';
import PageLayout from '../../../components/PageLayout/CarelonRx';
import SessionExpiryModal from '../../../components/modals/SessionExpiryModal';
import MfaService from '../../../services/mfa/MFAService';
import SecureSessionService from '../../../services/session/SecureSessionService';
import { useFlowNavigation, useFlow } from '../../../contexts/FlowContext';
import { ContactDetails, FactorType, InitiateOtpRequest } from '../../../types/mfa';
import { ORIGINAL_CLIENT_REDIRECT_URL } from '../../../config';
import { getLogoutRedirectUrl } from '../../../utils/nativeApp';
import '../../../styles/CarelonRx/login.css';
import '../../../styles/CarelonRx/mfa.css';

const TextVoiceSelectionPage: React.FC = () => {
  const { previousStep, nextStep, goToStep } = useFlowNavigation();
  const { setFlowData, getFlowData } = useFlow();
  const isNativeApp = getFlowData('device') === 'native_app';

  const [selectedDelivery, setSelectedDelivery] = useState<'text' | 'voice' | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [supportNumber, setSupportNumber] = useState<string>('');
  const [supportNumberError, setSupportNumberError] = useState<string>('');
  const [apiError, setApiError] = useState(false);

  const contactDetails = getFlowData('contactDetails') as ContactDetails | null;
  const selectedPhoneId = getFlowData('selectedPhoneId') as string;
  const storedDeliveryMethod = getFlowData('phoneDeliveryMethod') as 'text' | 'voice' | null;

  const selectedPhone = contactDetails?.phones.find(p => p.id === selectedPhoneId);
  const displayPhone = selectedPhone?.maskedNumber || '';

  const hasOnlyOnePhone = contactDetails &&
                          contactDetails.phones.length === 1 &&
     contactDetails.emails.length === 0;

  useEffect(() => {
    
    const storedSession = SecureSessionService.getStoredSession();
    if (storedSession?.expiresAt) {
      window.dispatchEvent(new CustomEvent('session-refreshed', { detail: { expiresAt: storedSession.expiresAt } }));
    }

    setSelectedDelivery(null);
    setFlowData('phoneDeliveryMethod', null);

    const cachedSupport = getFlowData('supportContact');
    const supportError = getFlowData('supportContactError');

    if (supportError) {
      setSupportNumberError('Unable to fetch support contact');
      setSupportNumber('');
    } else if (cachedSupport) {
      setSupportNumber(cachedSupport as string);
      setSupportNumberError('');
    } else {
      setSupportNumberError('Unable to fetch support contact');
    }
  }, []);

  const handleNext = async () => {
    if (!selectedDelivery) {
      setError('Please select how you would like to receive your code.');
      return;
    }

    const session = SecureSessionService.getStoredSession();
    if (!session) {
      window.dispatchEvent(new CustomEvent('session-expired'));
      return;
    }

    setIsSending(true);
    setError('');
    setApiError(false);

    try {
      const factorType: FactorType = selectedDelivery === 'text' ? 'SMS' : 'CALL';

      const phoneNumberRaw = selectedPhone?.fullNumber?.replace(/\D/g, '') || '';
      const phoneNumber = `+1${phoneNumberRaw}`;

      const request: InitiateOtpRequest = {
        factorType,
        contact: {
          phoneNumber
        }
      };

      const result = await MfaService.initiateOtp(request, session.sessionId);

      if (result.success) {
        setFlowData('phoneDeliveryMethod', selectedDelivery);
        setFlowData('mfaMethod', 'phone');
        setFlowData('mfaContactId', selectedPhoneId);
        setFlowData('mfaContactValue', displayPhone);
        setFlowData('factorId', result.factorId);
        setFlowData('factorType', factorType);
        setFlowData('selectedContact', phoneNumber);
        setFlowData('isFactorNewlyEnrolled', result.isFactorNewlyEnrolled);
        setTimeout(() => nextStep(), 0);
      } else {
        setError(result.message || 'Failed to send verification code. Please try again.');
      }
    } catch (err: any) {
      if (err.message?.includes('Session expired') || err.message?.includes('401')) {
        setError('Your session has expired. Please log in again.');
      } else {

        setApiError(true);
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleBack = () => {
    setError('');
    setApiError(false);

    const hasMultiplePhonesNoEmail = contactDetails &&
      contactDetails.phones.length > 1 &&
      contactDetails.emails.length === 0;

    if (hasOnlyOnePhone) {
      goToStep('webad-auth');
    } else if (hasMultiplePhonesNoEmail) {
      setFlowData('hasCompletedReview', false);
      goToStep('mfa-method');
    } else {
      goToStep('mfa-method');
    }
  };

  const hasPhones = contactDetails && contactDetails.phones.length > 0;
  const hasEmails = contactDetails && contactDetails.emails.length > 0;
  const hasBothOptions = hasPhones && hasEmails;

  const handleCancel = () => {
    if (hasBothOptions) {
      previousStep();
    } else {
      setShowCancelModal(true);
    }
  };

  const confirmCancel = () => {
    SecureSessionService.clearSession();
    const redirectUrl = isNativeApp
      ? getLogoutRedirectUrl()
      : ORIGINAL_CLIENT_REDIRECT_URL;
    window.location.replace(redirectUrl);
  };

  const dismissCancelModal = () => {
    setShowCancelModal(false);
  };

  return (
    <PageLayout bannerTitle="Security Verification for Login">
      <div className="login-page-container">
        <div className="login-left-panel">
          {!isNativeApp && (
            <a className="back-link mfa-back-link" onClick={handleBack}>
              <img src="/assets/carelonRx/images/Keyboard Arrow Left.svg" alt="" />
              <span>Back</span>
            </a>
          )}

          {!error && (
            <>
              <h1 className="mfa-title">
                To ensure your account's security, we will send you a verification code.
              </h1>
              <p className="mfa-subtitle">
                Choose how you would like to receive your code:
              </p>
            </>
          )}

          {error && (
            <div className="mfa-inline-error">
              <img src="/assets/carelonRx/images/error.svg" alt="Error" className="mfa-error-icon" />
              <p className="mfa-error-text">{error}</p>
            </div>
          )}

          <div className="mfa-methods">
            <div
              className={`mfa-method-option ${selectedDelivery === 'text' ? 'selected' : ''}`}
              onClick={() => setSelectedDelivery('text')}
            >
              <div className="mfa-method-header">
                <img
                  src={selectedDelivery === 'text'
                    ? "/assets/carelonRx/images/Selectors_clicked.svg"
                    : "/assets/carelonRx/images/Selectors.svg"
                  }
                  alt=""
                  className="mfa-selector-icon"
                />
                <div className="mfa-icon-content-wrapper">
                  <img src="/assets/carelonRx/images/sms.svg" alt="Text" className="mfa-method-icon" />
                  <div className="mfa-method-content">
                    <div className="mfa-method-label">Text</div>
                    <div className="mfa-contact-single">{displayPhone}</div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className={`mfa-method-option ${selectedDelivery === 'voice' ? 'selected' : ''}`}
              onClick={() => setSelectedDelivery('voice')}
            >
              <div className="mfa-method-header">
                <img
                  src={selectedDelivery === 'voice'
                    ? "/assets/carelonRx/images/Selectors_clicked.svg"
                    : "/assets/carelonRx/images/Selectors.svg"
                  }
                  alt=""
                  className="mfa-selector-icon"
                />
                <div className="mfa-icon-content-wrapper">
                  <img src="/assets/carelonRx/images/phone_in_talk.svg" alt="Voice" className="mfa-method-icon" />
                  <div className="mfa-method-content">
                    <div className="mfa-method-label">Voice</div>
                    <div className="mfa-contact-single">{displayPhone}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="disclaimer-section">
            {selectedDelivery === 'text' && (
              <div className="disclaimers" id="mfa-text-info">
                <img src="/assets/carelonRx/images/info.svg" alt="" className="mfa-info-icon" />
                <div className="mfa-disclaimer-text">
                  You'll receive a one-time text, so make sure your phone is able to receive text messages. Standard message and data rates may apply.
                </div>
              </div>
            )}
            {selectedDelivery === 'voice' && (
              <div className="disclaimers" id="mfa-voice-info">
                <img src="/assets/carelonRx/images/info.svg" alt="" className="mfa-info-icon" />
                <div className="mfa-disclaimer-text">
                  You'll receive a voice call with your verification code. Please ensure your ringer is on and be ready to answer your phone.
                </div>
              </div>
            )}
            <div className="disclaimers" id="mfa-info-disclaimer">
              {supportNumberError ? (
                <>
                  <img src="/assets/carelonRx/images/infoIconRed.svg" alt="Error" className="mfa-info-icon" />
                  <div className="mfa-disclaimer-text">
                    If your contact details are incorrect, please call the Pharmacy Member Services number on your member ID card. If you don't have your ID card, please check back later. We are currently experiencing an issue displaying the number and are working to fix it. Pharmacy Member Services is available 24/7.
                  </div>
                </>
              ) : (
                <>
                  <img src="/assets/carelonRx/images/info.svg" alt="" className="mfa-info-icon" />
                  <div className="mfa-disclaimer-text">
                    If we don't have the correct contact details to send your verification code, please contact Pharmacy Member Services <a href={`tel:${supportNumber}`} className="mfa-contact-phone-link">{supportNumber}</a> (TTY: 711) for assistance 24 hours a day, 7 days a week.
                  </div>
                </>
              )}
            </div>
            {apiError && (
              <div className="disclaimers" id="mfa-api-error">
                <img src="/assets/carelonRx/images/infoIconRed.svg" alt="Error" className="mfa-info-icon" />
                <div className="mfa-api-disclaimer-text">
                  Sorry, we're experiencing technical difficulties and can't send your verification code. Please try again later. If the issue continues, please call our Technical Support team at 1-844-430-0335 (TTY/TDD 711), Monday through Friday, from 8 a.m. to 8 p.m. ET, to repeat the issue.
                </div>
              </div>
            )}
          </div>

        </div>

        <div className="mfa-footer">
          <div className="mfa-footer-buttons">
            <button
              onClick={handleCancel}
              className={`crx-button crx-button-secondary ${hasBothOptions ? 'mfa-verify-another-way' : ''}`}
            >
              {hasBothOptions ? 'Verify Another Way' : 'Cancel'}
            </button>
            <button
              onClick={handleNext}
              disabled={!selectedDelivery || isSending}
              className="crx-button crx-button-primary"
            >
              {isSending ? 'Sending...' : 'Send My Code'}
            </button>
          </div>
        </div>
      </div>

      {showCancelModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="modal-title">Are you sure you want to cancel?</h2>
            <p className="modal-message">
              Doing so will prevent you from receiving a security verification code, which is necessary to log in.
            </p>
            <div className="modal-actions">
              <button onClick={dismissCancelModal} className="modal-btn-cancel">
                No, Don't Cancel
              </button>
              <button onClick={confirmCancel} className="modal-btn-stay">
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <SessionExpiryModal />
    </PageLayout>
  );
};

export default TextVoiceSelectionPage;
